import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardFooter,
  Button,
  Badge,
  Table,
  Alert,
} from "reactstrap";
import { toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageContainer from "../Layout/PageContainer";
import spaceService from "../../redux/services/space.service";

toast.configure();

const notify = (message, type = "error") =>
  toast(message, {
    transition: Slide,
    closeButton: true,
    autoClose: 3000,
    position: "top-right",
    type,
  });

const STATUS_BADGE = {
  valid: { color: "info", label: "Ready" },
  created: { color: "success", label: "Created (Draft)" },
  error: { color: "danger", label: "Error" },
};

const ImportSpaces = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]); // preview / result rows
  const [summary, setSummary] = useState(null); // { total, valid, invalid, created, committed }
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const committed = summary?.committed;

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const response = await spaceService.downloadImportTemplate();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "import_spaces_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notify("Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (event) => {
    const selected = event.target.files && event.target.files[0];
    // reset the input so selecting the same file again re-triggers change
    event.target.value = "";
    if (!selected) return;
    if (!/\.csv$/i.test(selected.name)) {
      notify("Please select a .csv file");
      return;
    }

    setFile(selected);
    setRows([]);
    setSummary(null);
    setPreviewing(true);
    try {
      const res = await spaceService.importSpaces(selected, false);
      const data = res?.data || {};
      setRows(data.rows || []);
      setSummary({
        total: data.total || 0,
        valid: data.valid || 0,
        invalid: data.invalid || 0,
        created: 0,
        committed: false,
      });
      notify(
        `${data.valid || 0} of ${data.total || 0} row(s) ready to import` +
          (data.invalid ? `, ${data.invalid} with errors` : ""),
        data.invalid ? "warning" : "success"
      );
    } catch (error) {
      const message =
        error?.response?.data?.message || error.message || "Failed to read CSV";
      notify(message);
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    try {
      const res = await spaceService.importSpaces(file, true);
      const data = res?.data || {};
      setRows(data.rows || []);
      setSummary({
        total: data.total || 0,
        valid: data.valid || 0,
        invalid: data.invalid || 0,
        created: data.created || 0,
        committed: true,
      });
      notify(
        `${data.created || 0} listing(s) created as Draft` +
          (data.invalid ? `, ${data.invalid} skipped` : ""),
        "success"
      );
    } catch (error) {
      const message =
        error?.response?.data?.message || error.message || "Import failed";
      notify(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setRows([]);
    setSummary(null);
  };

  const validCount = summary?.valid || 0;

  return (
    <PageContainer
      pageTitleIcon="pe-7s-home icon-gradient bg-plum-plate"
      pageHeading="Import Spaces"
      pageSubTitle="Upload a CSV to create multiple listings at once. Imported listings are saved as Draft for manual approval."
    >
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0">Import Spaces</h5>
            <div className="d-flex gap-2">
              <Button
                color="secondary"
                onClick={handleDownloadTemplate}
                disabled={downloading}
              >
                <i className="pe-7s-cloud-download me-1" />
                {downloading ? "Downloading…" : "Download Template"}
              </Button>
              <Button
                color="primary"
                onClick={handleImportClick}
                disabled={previewing || submitting}
              >
                <i className="pe-7s-upload me-1" />
                {previewing ? "Reading…" : "Import"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelected}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {file && (
            <p className="text-muted mb-3">
              <i className="pe-7s-file me-1" />
              {file.name}
            </p>
          )}

          {summary && (
            <div className="mb-3">
              <Badge color="secondary" className="me-2">
                {summary.total} total
              </Badge>
              {committed ? (
                <Badge color="success" className="me-2">
                  {summary.created} created
                </Badge>
              ) : (
                <Badge color="info" className="me-2">
                  {summary.valid} ready
                </Badge>
              )}
              {summary.invalid > 0 && (
                <Badge color="danger">{summary.invalid} with errors</Badge>
              )}
            </div>
          )}

          <Table responsive bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th style={{ width: "60px" }}>Row</th>
                <th>Title</th>
                <th style={{ width: "110px" }}>Host User ID</th>
                <th>Address</th>
                <th style={{ width: "140px" }}>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No listings loaded yet. Download the template, fill it in,
                    then click Import to preview the listings here.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const badge = STATUS_BADGE[row.status] || {
                    color: "secondary",
                    label: row.status,
                  };
                  return (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.title || <span className="text-muted">—</span>}</td>
                      <td>{row.hostUserId || <span className="text-muted">—</span>}</td>
                      <td>{row.address || <span className="text-muted">—</span>}</td>
                      <td>
                        <Badge color={badge.color}>{badge.label}</Badge>
                        {row.spaceId ? (
                          <span className="text-muted ms-1">#{row.spaceId}</span>
                        ) : null}
                      </td>
                      <td>
                        {row.errors && row.errors.length > 0 ? (
                          <ul className="mb-0 ps-3 text-danger small">
                            {row.errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {committed && (
            <Alert color="success" className="mt-3 mb-0">
              Import complete. Created listings are in <strong>Draft</strong>{" "}
              status and need manual approval from the Spaces list.
            </Alert>
          )}
        </CardBody>

        <CardFooter className="d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            onClick={committed ? () => navigate("/space/list") : handleReset}
            disabled={submitting || previewing}
          >
            {committed ? "Go to Spaces" : "Clear"}
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || previewing || committed || validCount === 0}
          >
            {submitting
              ? "Submitting…"
              : `Submit${validCount ? ` ${validCount}` : ""} Listing${
                  validCount === 1 ? "" : "s"
                }`}
          </Button>
        </CardFooter>
      </Card>
    </PageContainer>
  );
};

export default ImportSpaces;
